/**
 * time-group — pure date/time helpers for the Inbox (Inbox V2, spec §4.1/§4.5).
 *
 * Founder rule (Aspirine React#418 post-mortem): a server-fixed timezone in a
 * client render is a hydration-class bug. CHAOS UI is a Vite SPA (no SSR), so
 * there is no server render to drift from — but to be safe we still resolve the
 * viewer's timezone *at render* via Intl, never bake one at build, and format on
 * the client only. The viewer's local midnight (not UTC, not a fixed tz) is what
 * draws the Today / Yesterday / explicit-date boundaries.
 *
 * Everything here is pure and synchronous; callers pass `now` so the bucketing
 * is testable and stable within a render pass.
 */

/**
 * The viewer's resolved IANA timezone (e.g. "Europe/Moscow"). Resolved fresh on
 * each call so a runtime tz change (rare, but real on a travelling laptop or a
 * test that overrides it) is picked up at the next render. Falls back to UTC if
 * Intl is unavailable.
 */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Short local clock time, e.g. "9:42 AM" (or 24h, per the viewer's locale).
 * Tabular figures are applied by the caller via a `tabular-nums` class so the
 * column of times stays vertically aligned.
 */
export function formatLocalTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: viewerTimeZone(),
    }).format(d);
  } catch {
    return d.toLocaleTimeString();
  }
  // `now` is accepted for signature symmetry with dateBucket and future
  // "relative time" variants; unused today.
  void now;
}

/** Y-M-D triple in the viewer's local timezone (the day the user perceives). */
function localYMD(d: Date, tz: string): { y: number; m: number; day: number } {
  // en-CA yields ISO-ish "YYYY-MM-DD" parts; we read the numeric parts so the
  // comparison is locale-independent.
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: tz,
  }).formatToParts(d);
  const get = (t: string): number => Number(parts.find((p) => p.type === t)?.value ?? '0');
  return { y: get('year'), m: get('month'), day: get('day') };
}

/** Whole-day difference (a − b) measured in the viewer's local calendar. */
function dayDiff(a: Date, b: Date, tz: string): number {
  const ya = localYMD(a, tz);
  const yb = localYMD(b, tz);
  const ua = Date.UTC(ya.y, ya.m - 1, ya.day);
  const ub = Date.UTC(yb.y, yb.m - 1, yb.day);
  return Math.round((ua - ub) / 86_400_000);
}

export type DateBucket = 'today' | 'yesterday' | 'older';

/**
 * Classify an ISO timestamp into a coarse bucket relative to the viewer's local
 * "today". `older` rows are then labeled with {@link dateBucketLabel}.
 */
export function dateBucket(iso: string, now: Date = new Date()): DateBucket {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'older';
  const diff = dayDiff(now, d, viewerTimeZone());
  if (diff <= 0) return 'today'; // same local day (or a clock-skew future row)
  if (diff === 1) return 'yesterday';
  return 'older';
}

/**
 * The human group header for a row. "Today" / "Yesterday" for the two recent
 * buckets; for older rows an explicit date — "Jun 19" within the current year,
 * "Jun 19, 2025" once the year differs (so a year-old bullet is unambiguous).
 */
export function dateBucketLabel(iso: string, now: Date = new Date()): string {
  const b = dateBucket(iso, now);
  if (b === 'today') return 'Today';
  if (b === 'yesterday') return 'Yesterday';
  const d = new Date(iso);
  const tz = viewerTimeZone();
  const sameYear = localYMD(d, tz).y === localYMD(now, tz).y;
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
      timeZone: tz,
    }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}

/**
 * Stable per-day key for grouping (one header per local calendar day). Today and
 * Yesterday collapse to their labels; older days key by their local YMD so two
 * rows on the same day share a header and the order matches created_at DESC.
 */
export function dateGroupKey(iso: string, now: Date = new Date()): string {
  const b = dateBucket(iso, now);
  if (b === 'today') return 'today';
  if (b === 'yesterday') return 'yesterday';
  const d = new Date(iso);
  const { y, m, day } = localYMD(d, viewerTimeZone());
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
